import React from 'react'

import { useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router'

import AnalyticsPostCampaignReportPostList from '@components/Analytics/PostCampaignReport/Post/List/List'
import { AnalyticsPostCampaignReportReelList } from '@components/Analytics/PostCampaignReport/Reel/List/List'
import AnalyticsPostCampaignReportStoryList from '@components/Analytics/PostCampaignReport/Story/List/List'
import { AnalyticsPostCampaignReportSubmissionList } from '@components/Analytics/PostCampaignReport/Submission/List'
import AnalyticsPostCampaignReportTikTokList from '@components/Analytics/PostCampaignReport/TikTok/List/List'
import { AnalyticsPostCampaignReportXList } from '@components/Analytics/PostCampaignReport/X/List/List'
import { PostCampaignReportSubmissionIdProvider } from '@context/PostCampaignReportSubmissionId'
import { PCRChannels } from '@enums'
import { IApplicationState } from '@store'
import { selectSubmissionMetricIds } from '@store/postCampaignReport'
import { selectActivePCRChannel, selectCampaignId } from '@store/router'
import { Container } from '@tribegroup'
import PostCampaignReportSubmissionListRow from './Row/Row'

export interface IInternalProps {
  submissionMetricIds: ReadonlyArray<number>
  activeChannel: PCRChannels
  campaignId: number
}

const PostCampaignReportSubmissionList: React.FC<IInternalProps> = ({
  submissionMetricIds,
  activeChannel,
  campaignId,
}) => {
  if (activeChannel === PCRChannels.IGPosts) {
    return (
      <Container bottomInnerSpacing={4.5}>
        <AnalyticsPostCampaignReportPostList />
      </Container>
    )
  }

  if (activeChannel === PCRChannels.IGStories) {
    return (
      <Container bottomInnerSpacing={4.5}>
        <AnalyticsPostCampaignReportStoryList />
      </Container>
    )
  }

  if (activeChannel === PCRChannels.TikTok) {
    return (
      <Container bottomInnerSpacing={4.5}>
        <AnalyticsPostCampaignReportTikTokList />
      </Container>
    )
  }

  if (activeChannel === PCRChannels.Reel) {
    return (
      <Container bottomInnerSpacing={4.5}>
        <AnalyticsPostCampaignReportReelList />
      </Container>
    )
  }

  if (activeChannel === PCRChannels.X) {
    return (
      <Container bottomInnerSpacing={4.5}>
        <AnalyticsPostCampaignReportXList />
      </Container>
    )
  }

  if (
    [PCRChannels.Snap, PCRChannels.YouTubeShorts, PCRChannels.YouTubeVideos].includes(activeChannel)
  ) {
    return (
      <Container bottomInnerSpacing={4.5}>
        <AnalyticsPostCampaignReportSubmissionList activeChannel={activeChannel} />
      </Container>
    )
  }

  return (
    <Container topOuterSpacing={3.125} bottomInnerSpacing={4.5}>
      {submissionMetricIds.map((submissionMetricId) => (
        <PostCampaignReportSubmissionIdProvider
          key={`post-metrics-${submissionMetricId}`}
          submissionId={submissionMetricId}
        >
          <PostCampaignReportSubmissionListRow campaignId={campaignId} />
        </PostCampaignReportSubmissionIdProvider>
      ))}
    </Container>
  )
}

const PostCampaignReportSubmissionListConnected: React.FC = () => {
  const match = useRouteMatch()
  const campaignId = selectCampaignId(match)!
  const activeChannel = useSelector((state: IApplicationState) => selectActivePCRChannel(state))
  const submissionMetricIds = useSelector((state: IApplicationState) =>
    selectSubmissionMetricIds(state, campaignId, activeChannel),
  )
  return (
    <PostCampaignReportSubmissionList
      campaignId={campaignId}
      activeChannel={activeChannel}
      submissionMetricIds={submissionMetricIds}
    />
  )
}

export { PostCampaignReportSubmissionList }
export default PostCampaignReportSubmissionListConnected
